import { Link } from "@inertiajs/react"
import { ListChecks, SquarePen } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { briefCopy, briefPagePath } from "@/lib/openHouse/brief"

// The order page's Open House brief (OrderDetailPresenter `briefStatus`, nil
// when the order owes none): unfinished, its status, why it matters and the way
// to finish it (phase-1-wording §5); finished but still editable before
// payment, the way back into it (new-flow-wording §12); once paid, nothing.
// `inBundle`: beside the bundle's other brief, the writer brief's edit button
// names it (new-flow-wording §30).
export default function BriefStatusStrip({ orderId, brief, inBundle = false }) {
  if (!brief?.editable) return null

  const copy = briefCopy(brief.kind)
  const edit = inBundle && copy.editInBundle ? copy.editInBundle : copy.edit

  if (brief.complete) {
    return (
      <div className="mb-[14px] flex justify-end">
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link href={briefPagePath(orderId)}>
            <SquarePen className="size-3.5" strokeWidth={2} />
            {edit}
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div
      role="note"
      data-testid="brief-status"
      className="mb-[14px] flex flex-wrap items-center gap-x-[11px] gap-y-2 rounded-xl border border-needs-you/35 bg-needs-you/10 px-[18px] py-3 text-[13px] text-foreground max-[560px]:px-4"
    >
      <ListChecks className="size-4 shrink-0 text-needs-you" strokeWidth={1.9} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Badge variant="needsYou">{copy.status}</Badge>
        <span>{copy.statusHelp}</span>
      </div>
      <Button asChild size="sm" className="font-semibold max-[560px]:w-full">
        <Link href={briefPagePath(orderId)}>{copy.cta}</Link>
      </Button>
    </div>
  )
}

// A bundle's project page shows each brief the bundle owes (OrderDetailPresenter
// `bundleBriefs`: its other orders' briefs, each with its order), the writer
// brief before the cover brief, each with its own status and way in.
export function BriefStatusStrips({ order }) {
  const own = order.briefStatus ? [{ ...order.briefStatus, orderId: order.id }] : []
  const briefs = [...own, ...(order.bundleBriefs ?? [])]
    .sort((a, b) => (a.kind === "cover") - (b.kind === "cover"))

  return briefs.map((brief) => (
    <BriefStatusStrip key={brief.orderId} orderId={brief.orderId} brief={brief} inBundle={briefs.length > 1} />
  ))
}
